import { useEffect, useState, useContext } from "react";
import UserContext from "../UserContext";
import useApi from "../apiProvider";
import { Pagination, Table, Modal } from "antd";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCircleInfo } from "@fortawesome/free-solid-svg-icons";
import "./appStyle.css";

const ipToInteger = (ip) => {
  return ip
    .split(":")[0]
    .split(".")
    .reduce((acc, octet) => (acc << 8) + Number(octet), 0);
};

const alphanumericSort = (a, b) => {
  const regex = /\d+/g;
  const numA = a.userGmid.match(regex) || [];
  const numB = b.userGmid.match(regex) || [];
  if (numA.length && numB.length) {
    const numComp = parseInt(numA[0]) - parseInt(numB[0]);
    if (numComp !== 0) return numComp;
  }
  return a.userGmid.localeCompare(b.userGmid);
};

const formatDate = (dateString) => {
  if (dateString) {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat("en-GB", {
      year: "numeric",
      month: "short",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: false,
    }).format(date);
  }
};

const ResultsHistory = () => {
  const [data, setData] = useState([]);
  const [pageSize, setPageSize] = useState(10);
  const [current, setCurrent] = useState(1);
  const [tableLoading, setTableLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalData, setModalData] = useState([]);

  const userContext = useContext(UserContext);
  const userGmid = userContext?.uid;
  const isAdmin = userContext?.roles?.includes("app_admin");

  useEffect(() => {
    const getData = async () => {
      let results;
      if (isAdmin) results = await useApi.getAllSpeedTestResults();
      else results = await useApi.getSpeedTestResultsByGmid(userGmid);
      setData(results);
      setTableLoading(false);
    };
    setTableLoading(true);
    getData();
  }, [isAdmin, userGmid]);

  const openModal = (record) => {
    let subRecord = [
      { label: "Server", value: record?.serverHost },
      { label: "TCP Port", value: record?.serverPort },
      { label: "Server ID", value: record?.serverId },
      { label: "Server Version", value: record?.serverVersion },
      { label: "Test ID", value: record?.testId },
      { label: "Test Method", value: record?.testMethod },
    ];
    setModalData(subRecord);
    setIsModalOpen(true);
  };

  const columns = [
    {
      title: "",
      children: [
        isAdmin && {
          title: "GMID",
          dataIndex: "userGmid",
          key: "userGmid",
          sorter: (a, b) => alphanumericSort(a, b),
        },
        {
          title: "User IP",
          dataIndex: "userIp",
          key: "userIp",
          render: (text) => (text.includes(":") ? text.split(":")[0] : text),
          sorter: (a, b) => ipToInteger(a.userIp) - ipToInteger(b.userIp),
        },
      ],
    },
    {
      title: "Mbps", // Parent header
      children: [
        {
          title: "Download",
          dataIndex: "downloadSpeed",
          key: "downloadSpeed",
          align: "center",
          ////width: "8%",
          render: (downloadSpeed) =>
            (downloadSpeed ? downloadSpeed / 1024 : null).toFixed(1),
          sorter: (a, b) => a.downloadSpeed - b.downloadSpeed,
        },
        {
          title: "Upload",
          dataIndex: "uploadSpeed",
          key: "uploadSpeed",
          align: "center",
          //width: "8%",
          render: (uploadSpeed) =>
            (uploadSpeed ? uploadSpeed / 1024 : null).toFixed(1),
          sorter: (a, b) => a.uploadSpeed - b.uploadSpeed,
        },
      ],
    },
    {
      title: "ms", // Parent header
      children: [
        {
          title: "Latency",
          dataIndex: "minLatency",
          key: "minLatency",
          align: "center",
          //width: "8%",
          sorter: (a, b) => a.minLatency - b.minLatency,
        },
        {
          title: "Jitter",
          dataIndex: "latencyJitter",
          key: "latencyJitter",
          align: "center",
          //width: "8%",
          render: (latencyJitter) => latencyJitter?.toFixed(1),
          sorter: (a, b) => a.latencyJitter - b.latencyJitter,
        },
      ],
    },
    {
      title: "",
      children: [
        {
          title: "Start",
          dataIndex: "testStart",
          key: "testStart",
          render: (date) => formatDate(date),
          sorter: (a, b) => new Date(a.testStart) - new Date(b.testStart),
        },
        {
          title: "End",
          dataIndex: "testEnd",
          key: "testEnd",
          render: (date) => formatDate(date),
          sorter: (a, b) => new Date(a.testEnd) - new Date(b.testEnd),
        },
        {
          title: "Details",
          key: "details",
          //width: "8%",
          align: "center",
          render: (text, record) => (
            <>
              <FontAwesomeIcon
                icon={faCircleInfo}
                style={{ fontSize: "20px", color: "blue" }}
                onClick={(e) => openModal(record)}
              />
            </>
          ),
        },
      ],
    },
  ];

  const onChange = (page, pageSize) => {
    setCurrent(page);
    setPageSize(pageSize);
  };

  const paginatedData = data?.slice(
    (current - 1) * pageSize,
    current * pageSize
  );

  const rowClassName = (record, index) => {
    let rowClass = "";
    if (index % 2 === 0) {
      rowClass = "grayRow ";
    }

    // if (!record.id) {
    //   rowClass = rowClass + "emptyRow";
    // }

    return rowClass;
  };

  return (
    <div className="myTable">
      <Table
        size="small"
        rowKey={(record) => record.id}
        pagination={false}
        columns={isAdmin ? columns : columns.slice(1)}
        loading={tableLoading}
        dataSource={paginatedData}
        rowClassName={rowClassName}
      />
      <div className="tablePagination">
        <Pagination
          showSizeChanger
          current={current}
          pageSize={pageSize}
          total={data?.length}
          onChange={onChange}
          pageSizeOptions={["5", "10", "20", "50"]}
          size="small"
        />
      </div>
      <Modal
        title="Test Details"
        open={isModalOpen}
        onCancel={() => setIsModalOpen(false)}
        footer={null}
      >
        <table className="info-table">
          <tbody>
            {modalData.map((item, index) => {
              const rowClass = index % 2 === 0 ? "grey-row" : "white-row";
              return (
                <tr key={item.label} className={rowClass}>
                  <td className="label-cell">{item.label}</td>
                  <td className="data-cell">{item.value}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </Modal>
    </div>
  );
};

export default ResultsHistory;
